import {ReelConstants} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {Tween} from '@fantasmagames/core/src/tween/Tween';
import {Easing} from '@fantasmagames/core/src/tween/Easing';
import {View} from '@fantasmagames/slot/src/common/view/View';
import {SwipeInteractionViewEvents} from '@fantasmagames/slot/src/modules/swipeinteraction/SwipeInteractionViewEvents';

export class SwipeInteractionView extends View {
	// TODO: Needs to check PPI / DPI
	public static readonly MAX_Y_REEL_DRAG: number = 200;
	public static readonly MIN_Y_REEL_DRAG: number = 0;
	public static readonly BASE_POSITON: number = 0;
	public static readonly RESTORE_ANIMATION_DURATION: number = 200;

	public static readonly ACCELERATION_THRESHOLD: number = 1000; // Pixels per second needed to activate the spin.
	public static readonly DISTANCE_THRESHOLD: number = 199; // Pixels per second needed to activate the spin.

	protected _dragReelToSpinActive: boolean;
	protected _canDrag: boolean;
	protected _isDragging: boolean = false;

	protected _startDragPointX: number;
	protected _startDragPointY: number;
	protected _startTime: number = 0;
	protected _deltaTime: number;

	protected _interactiveContainerBasePositionY: number;

	protected _reelContainer: PIXI.Sprite;
	protected _interactiveContainer: PIXI.Sprite;

	public init(): void {
		super.init();

		this.initializeInteraction();
	}

	protected initializeInteraction(): void {
		this._reelContainer = new PIXI.Sprite(PIXI.Texture.EMPTY);
		this.container.addChild(this._reelContainer);

		this._interactiveContainerBasePositionY =
			ReelConstants.getReelsOffsets().y +
			(ReelConstants.MAX_ROW_AMOUNT - ReelConstants.ROW_AMOUNT) * ReelConstants.getSymbolSlot().height;
		this.setupInteractiveContainer();
	}

	public forceStopDrag(): void {
		if (!this._isDragging) {
			return;
		}

		this.restoreContainerAnimated();
		this.draggingFinished();
	}

	public resetPosition(): void {
		this._interactiveContainer.y = this._interactiveContainerBasePositionY;
		this._reelContainer.y = 0;
	}

	public levelChanged(level: number): void {
		const totalLevel = level + ReelConstants.ROW_AMOUNT;
		this._interactiveContainer.height = totalLevel * ReelConstants.getSymbolSlot().height;
		this._interactiveContainerBasePositionY =
			ReelConstants.getReelsOffsets().y + (ReelConstants.MAX_ROW_AMOUNT - totalLevel) * ReelConstants.getSymbolSlot().height;
		this._interactiveContainer.y = this._interactiveContainerBasePositionY;
	}

	public set dragReelToSpinActive(value: boolean) {
		if (this._interactiveContainer) {
			this._dragReelToSpinActive = this._interactiveContainer.buttonMode = value;
		}
	}

	public set canDrag(v: boolean) {
		if (this._interactiveContainer) {
			this._canDrag = this._interactiveContainer.interactive = v;
		}
	}

	public set reelContainer(reelContainer: PIXI.Container) {
		this._reelContainer.addChild(reelContainer);
		this.setupInteractiveContainer();
	}

	public set expandedWildsReelContainer(reelContainer: PIXI.Sprite) {
		this._reelContainer.addChild(reelContainer);
		// this.setupInteractiveContainer();
	}

	public set multiplierWildsReelContainer(reelContainer: PIXI.Sprite) {
		this._reelContainer.addChild(reelContainer);
		// this.setupInteractiveContainer();
	}

	protected setupInteractiveContainer(): void {
		// TODO: Decouple the whole SwipeInteraction system from reel. Separate into component.
		const reelBox = new PIXI.Graphics();
		reelBox.drawRect(
			0,
			0,
			ReelConstants.getSymbolSlot().width * ReelConstants.REELS_AMOUNT,
			ReelConstants.getSymbolSlot().height * ReelConstants.MAX_ROW_AMOUNT,
		);

		this._interactiveContainer = new PIXI.Sprite(reelBox.generateCanvasTexture());
		reelBox.destroy();

		this._interactiveContainer.height = ReelConstants.ROW_AMOUNT * ReelConstants.getSymbolSlot().height;

		this.container.addChild(this._interactiveContainer);
		this._interactiveContainer.position.set(ReelConstants.getReelsOffsets().x, this._interactiveContainerBasePositionY);

		if (this.device.isMobile) {
			this._interactiveContainer.on('touchstart', this.onDragStart.bind(this));
		} else {
			this._interactiveContainer.on('mousedown', this.onDragStart.bind(this));
		}
	}

	protected onDragStart(event: PIXI.interaction.InteractionEvent, ...args: any[]): void {
		if (!this._canDrag || !this._dragReelToSpinActive) {
			return;
		}

		this._startDragPointY = event.data.global.y;
		this._startDragPointX = event.data.global.x;
		this._startTime = event.data.originalEvent.timeStamp;

		if (this.device.isMobile) {
			this._interactiveContainer.on('touchend', this.onDragStop.bind(this));
			this._interactiveContainer.on('touchendoutside', this.onDragStop.bind(this));
			this._interactiveContainer.on('touchmove', this.onDragMoveEvent.bind(this));
		} else {
			this._interactiveContainer.on('mousemove', this.onDragMoveEvent.bind(this));
			this._interactiveContainer.on('rightup', this.onDragStop.bind(this));
			this._interactiveContainer.on('mouseup', this.onDragStop.bind(this));
			this._interactiveContainer.on('mouseout', this.onDragStop.bind(this));
		}

		this.draggingStarted(event);
	}

	protected onDragStop(event: PIXI.interaction.InteractionEvent, ...args: any[]): void {
		if (!this._isDragging) {
			return;
		}

		const endY = event.data.global.y;
		// const deltaY = Math.abs(endY - this._startDragPointY); // For allowing both upwards and downwards moving.
		const deltaY = endY - this._startDragPointY;
		const endTime = event.data.originalEvent.timeStamp;
		const deltaTime = (endTime - this._startTime) * 0.001;

		if (deltaY / deltaTime >= SwipeInteractionView.ACCELERATION_THRESHOLD || deltaY > SwipeInteractionView.DISTANCE_THRESHOLD) {
			this.emitStartSpinning();
		} else {
			this.restoreContainerAnimated();
		}

		this.draggingFinished();
	}

	protected onCancelDrag(event: PIXI.interaction.InteractionEvent, ...args: any[]): void {
		if (!this._isDragging) {
			return;
		}
		this.restoreContainerAnimated();
	}

	protected onDragMoveEvent(event: PIXI.interaction.InteractionEvent, ...args: any[]): void {
		const deltaY: number = event.data.global.y - this._startDragPointY;
		const newY: number = Math.max(Math.min(deltaY, SwipeInteractionView.MAX_Y_REEL_DRAG), SwipeInteractionView.MIN_Y_REEL_DRAG);

		this._interactiveContainer.y = this._interactiveContainerBasePositionY + newY;

		this._reelContainer.y = newY;

		if (newY > SwipeInteractionView.DISTANCE_THRESHOLD) {
			this.emitStartSpinning();
			this.draggingFinished();
		}
	}

	protected clearListeners(): void {
		if (this.device.isMobile) {
			this._interactiveContainer.removeListener('touchend');
			this._interactiveContainer.removeListener('touchendoutside');
			this._interactiveContainer.removeListener('touchmove');
		} else {
			this._interactiveContainer.removeListener('mousemove');
			this._interactiveContainer.removeListener('rightup');
			this._interactiveContainer.removeListener('mouseup');
			this._interactiveContainer.removeListener('mouseout');
		}
	}

	protected restoreContainerAnimated(): void {
		this._startDragPointX = null;
		this._startDragPointY = null;

		const restoreTween: Tween = new Tween(this._reelContainer.position)
			.to({y: SwipeInteractionView.BASE_POSITON}, SwipeInteractionView.RESTORE_ANIMATION_DURATION)
			.easing(Easing.Back.Out)
			.start();
		this.updateManager.add(restoreTween);

		const restoreInteractionTween: Tween = new Tween(this._interactiveContainer.position)
			.to({y: this._interactiveContainerBasePositionY}, SwipeInteractionView.RESTORE_ANIMATION_DURATION)
			.easing(Easing.Back.Out)
			.start();
		this.updateManager.add(restoreInteractionTween);
	}

	protected draggingStarted(event: PIXI.interaction.InteractionEvent): void {
		this._isDragging = true;
		this.emitDraggingStarted();
	}

	protected draggingFinished(): void {
		this.clearListeners();

		this._startDragPointX = null;
		this._startDragPointY = null;

		this.emitDraggingFinished();
	}

	protected emitDraggingStarted(): void {
		this.emit(SwipeInteractionViewEvents.DRAGGING_STARTED);
	}

	protected emitDraggingFinished(): void {
		this.emit(SwipeInteractionViewEvents.DRAGGING_FINISHED);
	}

	protected emitStartSpinning(): void {
		this.emit(SwipeInteractionViewEvents.START_SPIN);
	}

	protected cleanUpContainer(): void {
		if (this._interactiveContainer) {
			this._interactiveContainer.destroy();
		}
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/swipeinteraction/SwipeInteractionView.ts